import { navigate } from '@shef/native-bridge';
import { pathToRegexp } from 'path-to-regexp';
import { useEffect, useMemo } from 'react';
import { DispatchSendRequest } from 'src/mobile-app/hooks/types';
import { useHistory } from 'src/shared/hooks/useHistory';

/**
 * Bridges in-app navigations to the native shell: when the user transitions to a managed route,
 * the web navigation is blocked and re-dispatched to native instead.
 *
 * Transitions that only change the query string or hash (same pathname) are skipped — these are
 * in-page state updates (e.g. the meal-plan category carousel, search, sort, and filter pills),
 * not route navigations. Routing them through the native bridge drops the query string and, after
 * repeated changes, reloads the app back to its root.
 */
export function useNativeAppNavigation(dispatchSendRequest: DispatchSendRequest, managedRoutes: readonly string[]) {
  const history = useHistory();
  const regexps = useMemo(() => managedRoutes.map((route) => pathToRegexp(route)), [managedRoutes]);

  useEffect(() => {
    return history.block((location, action) => {
      if (location.pathname === history.location.pathname) return;
      for (const regexp of regexps) {
        if (regexp.test(location.pathname)) {
          dispatchSendRequest(navigate, { url: location.pathname, action });
          return false;
        }
      }
      // eslint-disable-next-line no-useless-return
      return;
    });
  }, [dispatchSendRequest, history, regexps]);
}
